import type { ReactNode } from 'react'

import { useSession } from '../session/SessionProvider.js'
import './shell.css'

export interface VoceMenu {
  chiave: string
  etichetta: string
  visibile: boolean
}

/**
 * L'impalcatura: barra in alto, navigazione a lato, area di lavoro.
 *
 * Il selettore della specie sta **nella barra**, accanto al nome
 * dell'associazione, e non dentro le singole schermate. È la scelta che
 * decide come si legge tutto il resto: cambiando specie cambiano le parole
 * («cucciolata» diventa «parto»), i campi obbligatori e le regole. Metterlo
 * in un filtro di una tabella lo farebbe sembrare un filtro, cioè una cosa
 * che restringe un elenco — e invece cambia il vocabolario dell'applicazione.
 *
 * Compare solo se l'associazione gestisce più di una specie: chi ne ha una
 * sola non deve scegliere niente.
 */
export function AppShell({
  voci,
  attiva,
  onNaviga,
  children,
}: {
  voci: readonly VoceMenu[]
  attiva: string
  onNaviga: (chiave: string) => void
  children: ReactNode
}) {
  const { context, species, multiSpecies, selectSpecies, logout, t } = useSession()
  const visibili = voci.filter((v) => v.visibile)

  return (
    <div className="guscio">
      {/* Chi naviga da tastiera attraversa il menu per intero a ogni pagina:
          dieci voci prima di arrivare al contenuto, ogni volta. Il collegamento
          è invisibile finché non riceve il fuoco. */}
      <a className="guscio__salta" href="#contenuto">
        Salta al contenuto
      </a>

      <header className="guscio__barra">
        <div className="guscio__marchio">
          <span className="guscio__nome">{t('app.name')}</span>
          {context?.tenant && <span className="guscio__tenant">{context.tenant.name}</span>}
        </div>

        <div className="guscio__azioni">
          {multiSpecies && (
            <label className="guscio__specie">
              <span className="solo-lettore">Specie</span>
              <select
                value={species?.code ?? ''}
                onChange={(event) => {
                  selectSpecies(event.target.value)
                }}
              >
                {context?.species.map((opzione) => (
                  <option key={opzione.code} value={opzione.code}>
                    {opzione.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          <button
            type="button"
            data-variante="piatto"
            onClick={() => {
              void logout()
            }}
          >
            Esci
          </button>
        </div>
      </header>

      <div className="guscio__corpo">
        {/* `aria-current` e non solo una classe: chi ascolta la pagina deve
            sapere dove si trova, non solo vederlo. */}
        <nav className="guscio__menu" aria-label="Sezioni">
          <ul>
            {visibili.map((voce) => (
              <li key={voce.chiave}>
                <button
                  type="button"
                  data-variante="piatto"
                  aria-current={voce.chiave === attiva ? 'page' : undefined}
                  onClick={() => {
                    onNaviga(voce.chiave)
                  }}
                >
                  {voce.etichetta}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <main className="guscio__lavoro">{children}</main>
      </div>
    </div>
  )
}
